import {z} from "zod";
import {
  TaskDetailSchema,
  TaskSummarySchema,
  UploadTargetSchema,
  type CreateDraftRequest,
  type TaskDetail,
  type TaskSummary,
  type UploadTarget
} from "@book-video-studio/contracts";
import {getAccessToken} from "./session";

const ErrorSchema = z.object({message: z.string().optional()});
const DraftResponseSchema = z.object({task: TaskDetailSchema, uploads: z.array(UploadTargetSchema)});
const TaskResponseSchema = z.object({task: TaskDetailSchema});
const TasksResponseSchema = z.object({tasks: z.array(TaskSummarySchema)});
const ResultDownloadResponseSchema = z.object({url: z.union([z.string().url(), z.string().startsWith("/api/v1/tasks/")])});

export class ApiError extends Error {
  public constructor(readonly status: number, message: string) {
    super(message);
  }
}

const request = async <T>(path: string, schema: z.ZodType<T>, init: RequestInit = {}): Promise<T> => {
  const token = getAccessToken();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(path, {...init, headers});
  const body: unknown = response.status === 204 ? null : await response.json();
  if (!response.ok) {
    const parsed = ErrorSchema.safeParse(body);
    throw new ApiError(response.status, parsed.success && parsed.data.message ? parsed.data.message : "请求失败");
  }
  return schema.parse(body);
};

export const verifySession = async (): Promise<void> => {
  await request("/api/v1/session/verify", z.object({valid: z.literal(true)}), {method: "POST"});
};

export const listTasks = async (trashed = false): Promise<TaskSummary[]> =>
  (await request(`/api/v1/tasks?limit=50&trashed=${trashed ? "true" : "false"}`, TasksResponseSchema)).tasks;

export const trashTasks = async (taskIds: string[]): Promise<void> => {
  await request(
    "/api/v1/tasks/trash",
    z.object({trashed: z.number().int().nonnegative()}),
    {method: "POST", body: JSON.stringify({taskIds})}
  );
};

export const deleteTasks = async (taskIds: string[]): Promise<void> => {
  await request(
    "/api/v1/tasks",
    z.object({deleted: z.number().int().nonnegative()}),
    {method: "DELETE", body: JSON.stringify({taskIds})}
  );
};

export const getTask = async (id: string): Promise<TaskDetail> =>
  (await request(`/api/v1/tasks/${id}`, TaskResponseSchema)).task;

export const getResultDownloadUrl = async (id: string): Promise<string> =>
  (await request(`/api/v1/tasks/${id}/result-download`, ResultDownloadResponseSchema)).url;

export const createDraft = async (input: CreateDraftRequest): Promise<{task: TaskDetail; uploads: UploadTarget[]}> =>
  request("/api/v1/tasks/drafts", DraftResponseSchema, {method: "POST", body: JSON.stringify(input)});

export const confirmUploaded = async (taskId: string, assetId: string): Promise<void> => {
  await request(`/api/v1/tasks/${taskId}/assets/${assetId}/uploaded`, z.object({verified: z.literal(true)}), {method: "POST"});
};

export const submitTask = async (taskId: string): Promise<TaskDetail> =>
  (await request(`/api/v1/tasks/${taskId}/submit`, TaskResponseSchema, {method: "POST"})).task;

export const cancelTask = async (taskId: string): Promise<TaskDetail> =>
  (await request(`/api/v1/tasks/${taskId}/cancel`, TaskResponseSchema, {method: "POST"})).task;

export const uploadFile = (
  target: UploadTarget,
  file: File,
  onProgress: (percent: number) => void
): Promise<void> => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open("PUT", target.url);
  xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
  xhr.upload.addEventListener("progress", (event) => {
    if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
  });
  xhr.addEventListener("load", () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      onProgress(100);
      resolve();
    } else {
      reject(new ApiError(xhr.status, `上传 ${file.name} 失败`));
    }
  });
  xhr.addEventListener("error", () => reject(new ApiError(0, `上传 ${file.name} 时网络中断`)));
  xhr.send(file);
});
